import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError, timer, of } from 'rxjs';
import { catchError, filter, finalize, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { AuthenticationService } from '../authentication.service';
import { authModel, storageApiKey } from '../models/auth';
import { FreezeOverlayService } from './freeze-overlay.service';
import { IpVerificationModalComponent } from 'src/app/modules/general/modals/ip-verification/ip-verification.modal';

type QueueItem = {
  request: HttpRequest<unknown>;
  handler: HttpHandler;
  attempts: number;
  subject: Subject<HttpEvent<unknown>>;
};

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private isTokenRequesting = false;
  private tokenSubject = new BehaviorSubject<authModel | null>(null);

  private queue: QueueItem[] = [];
  private draining = false;
  private infoSnackRef: MatSnackBarRef<unknown> | null = null;

  private didLogout = false;
  private exhaustedTransientCount = 0;
  private freezeHideTimer: ReturnType<typeof setTimeout> | null = null;

  private isVerifyingIp = false;
  private ipVerificationSubject = new BehaviorSubject<boolean | null>(null);

  private readonly MAX_ATTEMPTS = 6; // Increased from 4 for better gateway timeout recovery
  private readonly BASE_DELAY_MS = 1000;
  private readonly BACKOFF = 1.9;
  private readonly JITTER = 0.3;
  private readonly MAX_DELAY_CAP = 15000;
  private readonly MAX_TRANSIENT_EXHAUSTED = 20; // Increased from 12 for more tolerance
  private readonly MAX_QUEUE = 150;

  // prettier and maintainable version
  private readonly BYPASS_RE = new RegExp(
    [
      // === Auth endpoints ===
      '\\/oauth\\/',
      '\\/authentication\\/',

      // === Public/3rd-party APIs ===
      'ipify',
      'vision\\.googleapis\\.com',
      's3\\.eu-west-1\\.amazonaws\\.com',

      // === Client IP address verification (uses Basic Auth, not Bearer) ===
      '\\/client-ip-address\\/',

      // === AWS Lambda Function URLs ===
      '(?:^https?:\\/\\/)?[\\w-]+\\.lambda-url\\.[\\w-]+\\.on\\.aws(?:\\/|$)',

      // === API Gateway HTTP APIs ===
      '(?:^https?:\\/\\/)?[\\w-]+\\.execute-api\\.[\\w-]+\\.amazonaws\\.com(?:\\/|$)',

      // === Local or LAN targets (bell.local, 192.168.x.x, 10.x.x.x, 172.16–31.x.x) ===
      '^http:\\/\\/(?:bell\\.local|(?:192\\.168|10|172\\.(?:1[6-9]|2\\d|3[0-1]))(?:\\.\\d{1,3}){2})(?::\\d+)?\\b'
    ].join('|'),
    'i'
  );

  private readonly FORCE_LOGOUT_TITLES = new Set([
    'invalid_ipaddress',
    'ip_not_allowed',
    'ip_forbidden',
    'invalid_client',
    'invalid_grant',
    'invalid_token',
    'token_revoked'
  ]);

  constructor(
    public authenticationService: AuthenticationService,
    private snackBar: MatSnackBar,
    private freeze: FreezeOverlayService,
    private zone: NgZone,
    private dialog: MatDialog
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.zone.run(() => {
          this.hideFreezeOverlayDebounced();
          this.showRecoveredSnack();
          this.exhaustedTransientCount = 0;
          this.drainQueueIfNeeded();
        });
      });
      window.addEventListener('offline', () => {
        this.zone.run(() => this.freeze.show());
      });
    }
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.BYPASS_RE.test(request.url)) {
      return next.handle(request).pipe(
        catchError((err: HttpErrorResponse) => {
          if (this.isNetworkDown(err)) {
            this.freeze.show();
            this.showOfflineInfoSnack();
          } else if (err.status === 500) {
            this.showServer500Toast();
          } else if (this.shouldForceLogout(err)) {
            this.logoutWithMessage(this.logoutMessageFor(err));
          }
          return throwError(() => err);
        })
      );
    }

    const currentUser = this.safeGetUser();
    if (!currentUser) {
      this.logoutWithMessage('Je sessie is verlopen. Log opnieuw in.');
      return throwError(() => this.unauthorizedError(request));
    }

    // A stored user means a (new) login happened since the last logout; re-arm logout handling
    this.didLogout = false;

    if (!this.authenticationService.checkAccessTokenValidity()) {
      return this.handleUnauthorized(request, next);
    }

    const authedReq = request.clone({ setHeaders: { Authorization: `Bearer ${currentUser.access_token}` } });

    return next.handle(authedReq).pipe(
      tap({
        next: () => {
          // Hide freeze overlay on successful requests
          this.hideFreezeOverlayDebounced();
        }
      }),
      catchError((err: HttpErrorResponse) => {
        if (this.isNetworkDown(err)) return this.queueAndBridge(request, next);

        if (this.shouldForceLogout(err)) {
          this.logoutWithMessage(this.logoutMessageFor(err));
          return throwError(() => err);
        }

        if (this.isRecoverableIpMismatch(err)) {
          if (this.isVerifyingIp) {
            return this.ipVerificationSubject.pipe(
              filter(r => r !== null),
              take(1),
              switchMap(success => {
                if (!success) return throwError(() => err);
                return this.handleUnauthorized(request, next);
              })
            );
          }

          this.isVerifyingIp = true;
          this.ipVerificationSubject.next(null);

          const ipAddress = (err.error as { ip_address?: string; detail?: string })?.ip_address ?? (err.error as { ip_address?: string; detail?: string })?.detail ?? undefined;
          const dialogRef = this.dialog.open(IpVerificationModalComponent, { width: '450px', disableClose: true, data: { ipAddress } });
          return dialogRef.afterClosed().pipe(
            switchMap((verified: boolean) => {
              this.isVerifyingIp = false;
              this.ipVerificationSubject.next(verified ?? false);
              if (!verified) {
                this.logoutWithMessage('IP-verificatie geannuleerd of mislukt. Log opnieuw in.');
                return throwError(() => err);
              }
              return this.handleUnauthorized(request, next);
            })
          );
        }

        if (err.status === 500) {
          this.showServer500Toast();
          return throwError(() => err);
        }

        if (this.isTransient(err)) {
          return this.queueAndBridge(request, next);
        }

        if (err.status === 403) {
          this.showActionSnack('Er is een fout opgetreden');
          return throwError(() => err);
        }

        if (!this.authenticationService.checkAccessTokenValidity() && this.authenticationService.checkRefreshTokenValidity()) {
          return this.refreshTokenAndRetry(this.safeGetUser()!.refresh_token, request, next);
        }

        return this.handleUnauthorized(request, next);
      })
    );
  }

  private handleUnauthorized(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing || this.isTokenRequesting) {
      return this.tokenSubject.pipe(
        filter(r => r !== null),
        take(1),
        switchMap(m => next.handle(this.addTokenToRequest(request, m!.access_token)))
      );
    }

    const currentUser = this.safeGetUser();

    if (currentUser) {
      if (this.authenticationService.checkRefreshTokenValidity()) {
        this.isRefreshing = true;
        return this.refreshTokenAndRetry(currentUser.refresh_token, request, next);
      } else if (this.authenticationService.checkApiKeyValidity()) {
        const apiKey = this.safeGetApiKey();
        const snap = this.safeGetUser();
        if (apiKey && snap) {
          const username = (snap as authModel & { username?: string })?.username ?? '';
          const deviceToken = (snap as authModel & { device_token?: string })?.device_token ?? '';
          if (!username || !deviceToken) {
            this.showActionSnack('Sessieherstel mislukt: username/device_token ontbreekt. Log opnieuw in.');
            return throwError(() => this.unauthorizedError(request));
          }
          this.isRefreshing = true;
          this.isTokenRequesting = true;
          return this.loginViaApiKeyAndRetry(apiKey.api_key, username, deviceToken, request, next).pipe(
            finalize(() => (this.isTokenRequesting = false)),
            shareReplay(1)
          );
        }
      }
    }

    this.logoutWithMessage('Je sessie is verlopen. Log opnieuw in.');
    return throwError(() => this.unauthorizedError(request));
  }

  private refreshTokenAndRetry(refreshToken: string, request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.authenticationService.loginWithRefreshToken(refreshToken).pipe(
      switchMap((normalizedAuth: authModel) => {
        const bearer = normalizedAuth?.access_token ?? '';
        if (!bearer) return throwError(() => this.unauthorizedError(request));

        this.tokenSubject.next(normalizedAuth);

        const deviceToken = (normalizedAuth as authModel & { device_token?: string })?.device_token ?? (this.safeGetUser() as authModel & { device_token?: string })?.device_token ?? '';
        if (deviceToken) {
          this.authenticationService.requestApiKey(deviceToken).pipe(catchError(() => of(null))).subscribe(() => { });
        }

        return next.handle(this.addTokenToRequest(request, bearer));
      }),
      catchError((error: HttpErrorResponse) =>
        error.status === 401 && this.isTokenEndpointError(error)
          ? this.recoverFromRejectedRefresh(error, request, next)
          : this.handlePostAuthError(error, request, next)
      ),
      finalize(() => (this.isRefreshing = false)),
      shareReplay(1)
    );
  }

  private recoverFromRejectedRefresh(error: HttpErrorResponse, request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Another tab may already have rotated the refresh token and stored a fresh access token
    const fresh = this.safeGetUser();
    if (fresh?.access_token && this.authenticationService.checkAccessTokenValidity()) {
      return next.handle(this.addTokenToRequest(request, fresh.access_token));
    }

    // Fall back to the API key before giving up on the session
    const apiKey = this.safeGetApiKey();
    const username = (fresh as authModel & { username?: string })?.username ?? '';
    const deviceToken = (fresh as authModel & { device_token?: string })?.device_token ?? '';
    if (apiKey && username && deviceToken && !this.shouldForceLogout(error) && this.authenticationService.checkApiKeyValidity()) {
      return this.loginViaApiKeyAndRetry(apiKey.api_key, username, deviceToken, request, next);
    }

    return this.handlePostAuthError(error, request, next);
  }

  private loginViaApiKeyAndRetry(
    apiKey: string,
    username: string,
    deviceToken: string,
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.authenticationService.loginWithApiKey(apiKey, username, deviceToken).pipe(
      switchMap(() => {
        const stored = this.safeGetUser();
        const bearer = stored?.access_token ?? '';
        if (!bearer) {
          this.showActionSnack('Nieuw toegangstoken ontbreekt.');
          return throwError(() => this.unauthorizedError(request));
        }

        this.tokenSubject.next(stored!);

        const retried = this.addTokenToRequest(request, bearer);

        return this.authenticationService.requestApiKey(deviceToken).pipe(
          tap((k: storageApiKey) => { if (k?.api_key) this.safeSaveApiKey(k); }),
          catchError(() => of(null)),
          switchMap(() => next.handle(retried))
        );
      }),
      catchError((e: HttpErrorResponse) => this.handlePostAuthError(e, request, next)),
      finalize(() => (this.isRefreshing = false))
    );
  }

  private handlePostAuthError(error: HttpErrorResponse, request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (error.status === 400 && this.getProblemTitle(error) === 'invalid_request') {
      this.showActionSnack('Sessieherstel mislukt: ongeldig verzoek.');
      return throwError(() => error);
    }

    if (this.shouldForceLogout(error)) {
      this.logoutWithMessage(this.logoutMessageFor(error));
      return throwError(() => error);
    }

    // Token endpoint rejected both refresh token and API key: the session cannot be restored
    if (error.status === 401 && this.isTokenEndpointError(error)) {
      this.logoutWithMessage('Je sessie is verlopen. Log opnieuw in.');
      return throwError(() => error);
    }

    if (this.isNetworkDown(error)) return this.queueAndBridge(request, next);
    if (error.status === 500) { this.showServer500Toast(); return throwError(() => error); }
    if (this.isTransient(error)) return this.queueAndBridge(request, next);

    return throwError(() => error);
  }

  private getProblemTitle(err: HttpErrorResponse): string {
    if (!err) return '';
    try {
      const e: { title?: string; Title?: string; error?: string; error_description?: string } = err.error ?? {};
      const raw = (e.title ?? e.Title ?? e.error ?? e.error_description ?? '').toString();
      return raw.trim().toLowerCase();
    } catch { return ''; }
  }

  private isTokenEndpointError(err: HttpErrorResponse): boolean {
    return !!err.url && err.url.includes('/oauth/token/');
  }

  private isRecoverableIpMismatch(err: HttpErrorResponse): boolean {
    return err.status === 401 && this.getProblemTitle(err) === 'invalid_token_ip';
  }

  private shouldForceLogout(err: HttpErrorResponse): boolean {
    const title = this.getProblemTitle(err);
    return !!title && this.FORCE_LOGOUT_TITLES.has(title);
  }

  private logoutMessageFor(err: HttpErrorResponse): string {
    const title = this.getProblemTitle(err);
    switch (title) {
      case 'invalid_ipaddress':
      case 'ip_not_allowed':
      case 'ip_forbidden':
        return 'Je IP-adres is niet toegestaan. Log opnieuw in.';
      default:
        return 'Je sessie is ongeldig. Log opnieuw in.';
    }
  }

  private isTransient(err: HttpErrorResponse): boolean {
    if (err.status === 0) return true; // Network errors
    if (err.status === 500) return false; // Server errors - don't retry
    if (err.status === 504) return true; // Gateway timeout - retry
    if (err.status === 502) return true; // Bad gateway - retry
    if (err.status === 503) return true; // Service unavailable - retry
    if (err.status >= 501 && err.status <= 599) return true; // Other 5xx errors - retry
    if (err.status === 429) return true; // Rate limiting - retry
    return false;
  }

  private isGatewayTimeout(err: HttpErrorResponse): boolean {
    return err.status === 504 || err.status === 502 || err.status === 503;
  }

  private isNetworkDown(err: HttpErrorResponse): boolean {
    return err.status === 0 && (err.error instanceof ProgressEvent || !navigator.onLine);
  }

  private unauthorizedError(request: HttpRequest<unknown>): HttpErrorResponse {
    return new HttpErrorResponse({ status: 401, statusText: 'Unauthorized', url: request.urlWithParams });
  }

  private addTokenToRequest(request: HttpRequest<unknown>, token: string | null) {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private showActionSnack(message: string): void {
    this.snackBar.open(message, undefined, { duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom' });
  }

  private showServer500Toast(): void {
    this.snackBar.open('Actie mislukt door een serverfout.', undefined, { duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom' });
  }

  private showGatewayTimeoutToast(): void {
    this.snackBar.open('De server reageert traag (gateway timeout). Je acties worden opnieuw geprobeerd...', undefined, { duration: 4000, horizontalPosition: 'center', verticalPosition: 'bottom' });
  }

  private showOfflineInfoSnack(): void {
    if (this.infoSnackRef) return;
    this.infoSnackRef = this.snackBar.open('Geen verbinding met de server. Je acties worden gepauzeerd tot de verbinding terug is.', undefined, { duration: undefined, horizontalPosition: 'center', verticalPosition: 'bottom' });
  }

  private showRecoveredSnack(): void {
    if (this.infoSnackRef) { this.infoSnackRef.dismiss(); this.infoSnackRef = null; }
    this.snackBar.open('Verbonden! Acties worden hervat…', undefined, { duration: 2500, horizontalPosition: 'center', verticalPosition: 'bottom' });
  }

  private enqueue(item: QueueItem): void {
    if (this.queue.length >= this.MAX_QUEUE) this.queue.shift();
    this.queue.push(item);
  }

  private drainQueueIfNeeded(): void {
    if (this.draining) return;
    if (this.queue.length === 0) {
      // Hide overlay when queue is empty and we were draining
      if (this.draining) {
        this.draining = false;
        this.freeze.hide();
      }
      return;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    this.draining = true;

    const runRound = () => {
      if (this.queue.length === 0) {
        this.draining = false;
        this.hideFreezeOverlayDebounced();
        return;
      }

      const batch = [...this.queue];
      this.queue = [];
      let pending = batch.length;

      batch.forEach(item => {
        const delay = this.calcDelay(item.attempts);
        timer(delay).pipe(take(1)).subscribe(() => {
          const freshUser = this.safeGetUser();
          const token = freshUser?.access_token ?? null;
          const retryReq = token ? this.addTokenToRequest(item.request, token) : item.request;

          item.handler.handle(retryReq).subscribe({
            next: ev => {
              this.exhaustedTransientCount = 0;
              item.subject.next(ev);
            },
            error: (e: HttpErrorResponse) => {
              const offlineNow = this.isNetworkDown(e);
              const transient = this.isTransient(e) || offlineNow;
              const isGatewayTimeout = this.isGatewayTimeout(e);

              // Show user feedback for gateway timeouts on first retry
              if (isGatewayTimeout && item.attempts === 0) {
                this.showGatewayTimeoutToast();
              }

              if (transient && item.attempts + 1 < this.MAX_ATTEMPTS) {
                this.queue.push({ ...item, attempts: item.attempts + 1 });
              } else {
                if (!offlineNow && transient) {
                  this.exhaustedTransientCount++;
                  if (this.exhaustedTransientCount >= this.MAX_TRANSIENT_EXHAUSTED) {
                    this.logoutWithMessage('De server blijft onbereikbaar. Log opnieuw in.');
                    item.subject.error(e);
                    return;
                  }
                }

                // Hide overlay if this was the last item and it exhausted retries
                // but we're not logging out yet (tolerance for gateway timeouts)
                if (isGatewayTimeout && this.queue.length === 0 && this.exhaustedTransientCount < this.MAX_TRANSIENT_EXHAUSTED) {
                  this.hideFreezeOverlayDebounced();
                }

                item.subject.error(e);
              }
            },
            complete: () => item.subject.complete()
          }).add(() => {
            if (--pending === 0) runRound();
          });
        });
      });
    };

    runRound();
  }

  private calcDelay(attempt: number): number {
    const base = Math.min(this.BASE_DELAY_MS * Math.pow(this.BACKOFF, attempt), this.MAX_DELAY_CAP);
    const jitter = base * this.JITTER * (Math.random() * 2 - 1);
    return Math.max(300, Math.round(base + jitter));
  }

  private queueAndBridge(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const subject = new Subject<HttpEvent<unknown>>();
    this.enqueue({ request, handler: next, attempts: 0, subject });

    // Cancel any pending hide timer
    if (this.freezeHideTimer) {
      clearTimeout(this.freezeHideTimer);
      this.freezeHideTimer = null;
    }

    this.freeze.show();
    this.showOfflineInfoSnack();
    this.drainQueueIfNeeded();
    return subject.asObservable();
  }

  private hideFreezeOverlayDebounced(): void {
    // Cancel any existing timer
    if (this.freezeHideTimer) {
      clearTimeout(this.freezeHideTimer);
    }

    // Only hide if queue is empty and not draining
    if (this.queue.length === 0 && !this.draining) {
      // Add a small delay to prevent flashing
      this.freezeHideTimer = setTimeout(() => {
        if (this.queue.length === 0 && !this.draining) {
          this.freeze.hide();
        }
        this.freezeHideTimer = null;
      }, 500);
    }
  }

  private safeGetUser(): authModel | null {
    try { return JSON.parse(localStorage.getItem('auth') || 'null'); } catch { return null; }
  }

  private safeGetApiKey(): storageApiKey | null {
    try { return JSON.parse(localStorage.getItem('apiKey') || 'null'); } catch { return null; }
  }

  private safeSaveApiKey(k: storageApiKey) {
    try { localStorage.setItem('apiKey', JSON.stringify(k)); } catch { }
  }

  private logoutWithMessage(message: string) {
    if (this.didLogout) return;
    this.didLogout = true;

    const err = new HttpErrorResponse({ status: 401, statusText: 'Logged out' });
    try { for (const item of this.queue) item.subject.error(err); } catch { }
    this.queue = [];
    this.draining = false;

    // Clean up timer
    if (this.freezeHideTimer) {
      clearTimeout(this.freezeHideTimer);
      this.freezeHideTimer = null;
    }

    this.freeze.hide();
    if (this.infoSnackRef) { this.infoSnackRef.dismiss(); this.infoSnackRef = null; }
    this.authenticationService.logout();

    this.snackBar.open(message, undefined, { duration: 5000, horizontalPosition: 'center', verticalPosition: 'bottom' });
  }
}
